<template>
  <div class="min-h-screen text-foreground py-12 relative z-20">
    <div class="container mx-auto px-4 max-w-5xl">
      <div class="text-center mb-10">
        <h1 class="text-4xl md:text-5xl font-bold text-white mb-4">{{ $t('bookAppointment') }}</h1>
        <p class="text-lg font-light text-gray-300">{{ $t('bookAppointmentSubtitle') }}</p>
        <p v-if="invite && invite.client_name" class="mt-4 text-teal-300">
          {{ $t('welcomeClient', { name: invite.client_name }) }}
        </p>
      </div>

      <!-- Invalid personal link -->
      <div v-if="inviteError"
        class="bg-gray-900/70 backdrop-blur-md p-6 md:p-8 rounded-xl border border-red-800 shadow-lg text-center">
        <font-awesome-icon :icon="['fas', 'link-slash']" class="text-4xl text-red-400 mb-4" />
        <p class="text-gray-300">{{ $t('invalidBookingLink') }}</p>
      </div>

      <!-- Confirmation -->
      <div v-else-if="confirmed"
        class="bg-gray-900/70 backdrop-blur-md p-6 md:p-8 rounded-xl border border-teal-500 shadow-lg text-center">
        <font-awesome-icon :icon="['fas', 'circle-check']" class="text-5xl text-teal-400 mb-4" />
        <h2 class="text-2xl md:text-3xl font-bold text-teal-400 mb-2">{{ $t('appointmentConfirmed') }}</h2>
        <p class="text-gray-300">{{ formatLongDate(confirmed.date) }} — {{ confirmed.time }}</p>
        <p class="text-gray-400 text-sm mt-4">{{ $t('confirmationEmailSent', { email: confirmed.email }) }}</p>
      </div>

      <template v-else>
        <div class="grid gap-6 md:grid-cols-2">
          <!-- Step 1: Date -->
          <div
            class="bg-gray-900/70 backdrop-blur-md p-6 rounded-xl border border-gray-700 shadow-lg transition-all duration-300 hover:border-teal-500">
            <h2 class="text-xl font-bold text-teal-400 mb-4">
              <span class="step-badge">1</span>{{ $t('selectDate') }}
            </h2>

            <div class="flex items-center justify-between mb-4">
              <button @click="changeMonth(-1)" :disabled="!canGoBack"
                class="w-9 h-9 rounded-lg bg-gray-800 border border-gray-700 text-gray-300 transition-colors"
                :class="canGoBack ? 'hover:bg-gray-700' : 'opacity-30 cursor-not-allowed'"
                :aria-label="$t('previousMonth')">
                <font-awesome-icon :icon="['fas', 'chevron-left']" />
              </button>
              <span class="text-white font-semibold capitalize">{{ monthLabel }}</span>
              <button @click="changeMonth(1)"
                class="w-9 h-9 rounded-lg bg-gray-800 border border-gray-700 text-gray-300 hover:bg-gray-700 transition-colors"
                :aria-label="$t('nextMonth')">
                <font-awesome-icon :icon="['fas', 'chevron-right']" />
              </button>
            </div>

            <div class="grid grid-cols-7 gap-1 text-center text-xs font-semibold uppercase text-gray-400 mb-2">
              <span v-for="day in weekdayLabels" :key="day">{{ day }}</span>
            </div>
            <div class="grid grid-cols-7 gap-1">
              <span v-for="n in leadingBlanks" :key="'blank-' + n"></span>
              <button v-for="day in monthDays" :key="day.iso" @click="selectDate(day.iso)" :disabled="day.past"
                class="h-10 rounded-lg text-sm font-medium transition-colors" :class="dayClass(day)">
                {{ day.number }}
              </button>
            </div>
          </div>

          <!-- Step 2: Time -->
          <div
            class="bg-gray-900/70 backdrop-blur-md p-6 rounded-xl border border-gray-700 shadow-lg transition-all duration-300 hover:border-teal-500">
            <h2 class="text-xl font-bold text-teal-400 mb-4">
              <span class="step-badge">2</span>{{ $t('selectTime') }}
            </h2>

            <p v-if="!selectedDate" class="text-gray-400">{{ $t('pickDateFirst') }}</p>
            <div v-else-if="slotsLoading" class="text-center text-white mt-8">
              <font-awesome-icon icon="spinner" spin class="text-2xl" />
            </div>
            <template v-else>
              <p class="text-gray-300 mb-4 capitalize">{{ formatLongDate(selectedDate) }}</p>
              <div class="grid grid-cols-3 sm:grid-cols-4 gap-2">
                <button v-for="slot in slots" :key="slot.time" @click="selectedTime = slot.time"
                  :disabled="!slot.available"
                  class="py-2 rounded-lg text-sm font-medium border transition-colors" :class="slotClass(slot)">
                  {{ slot.time }}
                </button>
              </div>
              <p v-if="!slots.some(s => s.available)" class="text-gray-400 mt-4">{{ $t('noSlotsLeft') }}</p>
            </template>
          </div>
        </div>

        <!-- Step 3: Details -->
        <transition name="fade">
          <form v-if="selectedTime" @submit.prevent="submit"
            class="mt-6 space-y-4 bg-gray-900/70 backdrop-blur-md p-6 md:p-8 rounded-xl border border-gray-700 shadow-lg transition-all duration-300 hover:border-teal-500">
            <h2 class="text-xl font-bold text-teal-400">
              <span class="step-badge">3</span>{{ $t('yourDetails') }}
            </h2>
            <p class="text-gray-300">
              <font-awesome-icon :icon="['fas', 'calendar']" class="mr-2 text-teal-400" />
              <span class="capitalize">{{ formatLongDate(selectedDate) }}</span> — {{ selectedTime }} (30 min)
            </p>

            <div class="grid gap-4 md:grid-cols-2">
              <div>
                <label for="appt-name" class="block text-base font-semibold text-teal-300 mb-2">{{ $t('yourName') }}</label>
                <input v-model="form.name" id="appt-name" type="text" :placeholder="$t('namePlaceholder')"
                  class="w-full px-4 py-2 md:py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                  required />
              </div>
              <div>
                <label for="appt-email" class="block text-base font-semibold text-teal-300 mb-2">{{ $t('yourEmail') }}</label>
                <input v-model="form.email" id="appt-email" type="email" :placeholder="$t('emailPlaceholder')"
                  class="w-full px-4 py-2 md:py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                  required />
              </div>
            </div>
            <div>
              <label for="appt-notes" class="block text-base font-semibold text-teal-300 mb-2">{{ $t('appointmentNotes') }}</label>
              <textarea v-model="form.notes" id="appt-notes" rows="3" :placeholder="$t('appointmentNotesPlaceholder')"
                class="w-full px-4 py-2 md:py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"></textarea>
            </div>

            <button type="submit" :disabled="submitting" :class="{ 'opacity-60': submitting }"
              class="w-full py-2 md:py-3 bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white font-medium rounded-lg transition-all duration-300 flex items-center justify-center space-x-2">
              <font-awesome-icon :icon="submitting ? 'spinner' : ['fas', 'calendar-check']" :spin="submitting" />
              <span>{{ $t('confirmAppointment') }}</span>
            </button>

            <div v-if="error"
              class="p-3 rounded-lg text-center text-sm font-medium bg-red-900/30 text-red-400 border border-red-800">
              {{ error }}
            </div>
          </form>
        </transition>
      </template>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import { fetchAvailability, bookAppointment, fetchBookingLink } from "@/api/appointments";

// Local YYYY-MM-DD (toISOString would shift the day for non-UTC timezones)
const toIso = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default {
  name: "AppointmentPage",
  setup() {
    const route = useRoute();
    const { t, locale } = useI18n();

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const viewMonth = ref(new Date(today.getFullYear(), today.getMonth(), 1));

    const selectedDate = ref(null);
    const selectedTime = ref(null);
    const slots = ref([]);
    const slotsLoading = ref(false);

    const form = ref({ name: "", email: "", notes: "" });
    const submitting = ref(false);
    const error = ref("");
    const confirmed = ref(null);

    const token = route.params.token || null;
    const invite = ref(null);
    const inviteError = ref(false);

    const monthLabel = computed(() =>
      viewMonth.value.toLocaleDateString(locale.value, { month: "long", year: "numeric" })
    );

    // Monday-first week
    const weekdayLabels = computed(() => {
      // 2024-01-01 was a Monday
      return Array.from({ length: 7 }, (_, i) =>
        new Date(2024, 0, 1 + i).toLocaleDateString(locale.value, { weekday: "short" })
      );
    });

    const leadingBlanks = computed(() => (viewMonth.value.getDay() + 6) % 7);

    const monthDays = computed(() => {
      const year = viewMonth.value.getFullYear();
      const month = viewMonth.value.getMonth();
      const count = new Date(year, month + 1, 0).getDate();
      return Array.from({ length: count }, (_, i) => {
        const date = new Date(year, month, i + 1);
        return { number: i + 1, iso: toIso(date), past: date < today };
      });
    });

    const canGoBack = computed(
      () => viewMonth.value > new Date(today.getFullYear(), today.getMonth(), 1)
    );

    const changeMonth = (delta) => {
      const d = viewMonth.value;
      viewMonth.value = new Date(d.getFullYear(), d.getMonth() + delta, 1);
    };

    const loadSlots = async () => {
      slotsLoading.value = true;
      try {
        slots.value = await fetchAvailability(selectedDate.value);
      } catch (err) {
        console.error("Error fetching availability:", err);
        slots.value = [];
      } finally {
        slotsLoading.value = false;
      }
    };

    const selectDate = (iso) => {
      selectedDate.value = iso;
      selectedTime.value = null;
      error.value = "";
      loadSlots();
    };

    const formatLongDate = (iso) => {
      const [y, m, d] = iso.split("-").map(Number);
      return new Date(y, m - 1, d).toLocaleDateString(locale.value, {
        weekday: "long", day: "numeric", month: "long", year: "numeric",
      });
    };

    const dayClass = (day) => {
      if (day.iso === selectedDate.value) return "bg-teal-500 text-white";
      if (day.past) return "text-gray-600 cursor-not-allowed";
      return "bg-gray-800 text-gray-200 hover:bg-gray-700 border border-gray-700";
    };

    const slotClass = (slot) => {
      if (slot.time === selectedTime.value) return "bg-teal-500 border-teal-400 text-white";
      if (!slot.available) return "bg-gray-900 border-gray-800 text-gray-600 line-through cursor-not-allowed";
      return "bg-gray-800 border-gray-700 text-gray-200 hover:border-teal-500 hover:text-teal-300";
    };

    const submit = async () => {
      submitting.value = true;
      error.value = "";
      try {
        const appointment = await bookAppointment({
          date: selectedDate.value,
          time: selectedTime.value,
          name: form.value.name,
          email: form.value.email,
          notes: form.value.notes || null,
          token,
        });
        confirmed.value = appointment;
      } catch (err) {
        const status = err.response?.status;
        if (status === 409) {
          error.value = t("slotTaken");
          selectedTime.value = null;
          loadSlots();
        } else if (status === 410) {
          inviteError.value = true;
        } else {
          error.value = err.response?.data?.detail?.toString() || t("bookingFailed");
        }
      } finally {
        submitting.value = false;
      }
    };

    onMounted(async () => {
      if (!token) return;
      try {
        invite.value = await fetchBookingLink(token);
        form.value.name = invite.value.client_name || "";
        form.value.email = invite.value.client_email || "";
      } catch {
        inviteError.value = true;
      }
    });

    return {
      monthLabel, weekdayLabels, leadingBlanks, monthDays, canGoBack, changeMonth,
      selectedDate, selectedTime, slots, slotsLoading, selectDate, formatLongDate,
      dayClass, slotClass, form, submitting, error, confirmed, submit, invite, inviteError,
    };
  },
};
</script>

<style scoped>
.step-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  margin-right: 0.75rem;
  border-radius: 9999px;
  border: 1px solid #38b2ac;
  /* teal-500 */
  font-size: 0.875rem;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
